import React, { useState, useEffect } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL, listAll, deleteObject, getMetadata } from "firebase/storage";
import { auth } from "../firebase-config";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Typography, Tooltip, CircularProgress, Tabs, Tab } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import { Document, Page, pdfjs } from 'react-pdf';
import dayjs from 'dayjs';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FileStorage = () => {
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [openedPDFs, setOpenedPDFs] = useState([]);  // Track opened PDF files
  const [selectedTab, setSelectedTab] = useState(0); // Track currently selected tab
  const [scale, setScale] = useState(0.8);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    if (auth.currentUser) {
      setUserId(auth.currentUser.uid);
      fetchUserFiles();
    }
  }, [auth.currentUser]);

  const fetchUserFiles = async () => {
    setIsLoading(true);
    try {
      const storageRef = ref(getStorage(), `userFiles/${auth.currentUser.uid}`);
      const fileList = await listAll(storageRef);
      const files = await Promise.all(
        fileList.items.map(async (fileRef) => {
          const url = await getDownloadURL(fileRef);
          const metadata = await getMetadata(fileRef);
          return { name: fileRef.name, url, metadata };
        })
      );
      setUploadedFiles(files);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching files:', error);
      setError('Failed to fetch files.');
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !userId) return;
    setUploadProgress(0);
    setIsLoading(true);
    const storageRef = ref(getStorage(), `userFiles/${userId}/${file.name}`);

    try {
      const uploadTask = uploadBytes(storageRef, file);
      await uploadTask;
      fetchUserFiles();
      setIsLoading(false);
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload the file.');
      setIsLoading(false);
    }
  };

  const handleDelete = async (fileName) => {
    const fileRef = ref(getStorage(), `userFiles/${userId}/${fileName}`);
    try {
      await deleteObject(fileRef);
      fetchUserFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Failed to delete the file.');
    }
  };

  const handleFileClick = (fileUrl, fileType, fileName) => {
    if (fileType === 'application/pdf') {
      const existingIndex = openedPDFs.findIndex(pdf => pdf.url === fileUrl);
      if (existingIndex === -1) {
        setOpenedPDFs([...openedPDFs, { name: fileName, url: fileUrl, pageNumber: 1, numPages: null }]);
        setSelectedTab(openedPDFs.length); // Select the new tab
      } else {
        setSelectedTab(existingIndex); // Switch to the existing tab
      }
    } else {
      window.open(fileUrl, '_blank');
    }
  };

  const closeTab = (index) => {
    const newOpenedPDFs = [...openedPDFs];
    newOpenedPDFs.splice(index, 1);
    setOpenedPDFs(newOpenedPDFs);
    setSelectedTab(Math.max(0, selectedTab - 1)); // Select the previous tab if possible
  };

  const onDocumentLoadSuccess = (index, { numPages }) => {
    const updatedPDFs = [...openedPDFs];
    updatedPDFs[index].numPages = numPages;
    setOpenedPDFs(updatedPDFs);
  };

  const changePage = (index, offset) => {
    const updatedPDFs = [...openedPDFs];
    updatedPDFs[index].pageNumber += offset;
    setOpenedPDFs(updatedPDFs);
  };

  const previousPage = (index) => {
    changePage(index, -1);
  };

  const nextPage = (index) => {
    changePage(index, 1);
  };

  const zoomIn = () => {
    setScale(scale + 0.1);
  };

  const zoomOut = () => {
    setScale(scale - 0.1);
  };

  const rotateClockwise = () => {
    setRotation(rotation + 90);
  };

  const rotateCounterClockwise = () => {
    setRotation(rotation - 90);
  };

  const downloadFile = (fileUrl) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = 'download.pdf';
    link.click();
  };

  const printFile = (fileUrl) => {
    window.open(fileUrl, '_blank');
  };

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      {/* File Storage Section */}
      <div style={{ width: openedPDFs.length > 0 ? '100%' : '70%', padding: '0px', overflowY: 'auto' }}>
        <Typography variant="h4" gutterBottom>
          User Cloud Storage
        </Typography>
        {userId ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
              <input
                type="file"
                onChange={(e) => handleFileUpload(e.target.files[0])}
                style={{ marginRight: '10px' }}
              />
              <IconButton onClick={handleFileUpload}>
                <UploadIcon />
              </IconButton>
            </div>

            {isLoading && (
              <div style={{ marginBottom: '20px' }}>
                <CircularProgress />
                {uploadProgress > 0 && <Typography variant="body2">{`Upload Progress: ${uploadProgress}%`}</Typography>}
              </div>
            )}

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>File Type</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Upload Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uploadedFiles.map((file, index) => (
                    <TableRow key={index}>
                      <TableCell>{file.name}</TableCell>
                      <TableCell>{file.metadata.contentType || 'Unknown'}</TableCell>
                      <TableCell>{(file.metadata.size / 1024).toFixed(2)} KB</TableCell>
                      <TableCell>{dayjs(file.metadata.timeCreated).format('MMM D, YYYY h:mm A')}</TableCell>
                      <TableCell>
                        <Tooltip title="Open in Viewer">
                          <IconButton onClick={() => handleFileClick(file.url, file.metadata.contentType, file.name)}>
                            <UploadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton onClick={() => handleDelete(file.name)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Typography variant="h6" color="textSecondary">
            Please log in to view your cloud storage.
          </Typography>
        )}
      </div>

      {/* PDF Viewer Section */}
      {openedPDFs.length > 0 && (
        <div style={{ width: '66%', borderLeft: '1px solid #ccc', overflow: 'hidden' }}>
          <Tabs value={selectedTab} onChange={(event, newValue) => setSelectedTab(newValue)} indicatorColor="primary">
            {openedPDFs.map((pdf, index) => (
              <Tab
                key={index}
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {pdf.name}
                    <IconButton size="small" onClick={() => closeTab(index)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                }
              />
            ))}
          </Tabs>
          
          <div style={{ padding: '0px', height: 'calc(100vh - 50px)', overflowY: 'auto' }}>
            {openedPDFs.map((pdf, index) =>
              index === selectedTab ? (
                <div key={index}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0px' }}>
                    <div>
                      <IconButton onClick={() => previousPage(index)} disabled={pdf.pageNumber <= 1}>
                        <ArrowBackIosNewIcon />
                      </IconButton>
                      <Typography variant="body2" display="inline">{`Page ${pdf.pageNumber} of ${pdf.numPages || '?'}`}</Typography>
                      <IconButton onClick={() => nextPage(index)} disabled={pdf.pageNumber >= pdf.numPages}>
                        <ArrowForwardIosIcon />
                      </IconButton>
                    </div>
                    <div>
                      <IconButton onClick={zoomOut}>
                        <ZoomOutIcon />
                      </IconButton>
                      <IconButton onClick={zoomIn}>
                        <ZoomInIcon />
                      </IconButton>
                      <IconButton onClick={rotateCounterClockwise}>
                        <RotateLeftIcon />
                      </IconButton>
                      <IconButton onClick={rotateClockwise}>
                        <RotateRightIcon />
                      </IconButton>
                      <IconButton onClick={() => downloadFile(pdf.url)}>
                        <DownloadIcon />
                      </IconButton>
                      <IconButton onClick={() => printFile(pdf.url)}>
                        <PrintIcon />
                      </IconButton>
                    </div>
                  </div>

                  {/* Scrollable PDF Document */}
                  <div style={{ height: '85vh', overflowY: 'auto' }}>
                    <Document
                      file={pdf.url}
                      onLoadSuccess={(doc) => onDocumentLoadSuccess(index, doc)}
                      rotate={rotation}
                    >
                      <Page pageNumber={pdf.pageNumber} scale={scale} />
                    </Document>
                  </div>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileStorage;
